import { Box, Stack, Typography, Switch, IconButton } from "@mui/material";
import { useState, useEffect } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Flow from "./Flow";
import Nodes from "./Nodes";
import ButtonOne from "../../common/normal-button";
import CustomTabs from "../../common/custom-tabs";
import { ColorsConstants } from "../../cssUtils/constants";
import { useAuth } from "../../hooks/AuthProvider";
import { NODEBOT_NODEBOTS_BULK_UPDATE } from "../../config/urls";
import { Snackbar, Alert, CircularProgress } from "@mui/material";
import BackButton from "../../common/back-button";

const ChatbotFlow = (props) => {
  const { getValidToken } = useAuth();
  const [activeTab, setActiveTab] = useState(0); // 0 = Flow, 1 = Nodes
  const [activateWorkflow, setActivateWorkflow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const workflowId = props.pageParams?.workflowId;
  const workflowName =
    props.pageParams?.workflowName || "Untitled Workflow";
  // Get the workflow status from pageParams
  const workflowStatus = props.pageParams?.isActive || false;
  const tenant = localStorage.getItem("tenant");

  useEffect(() => {
    // Set the workflow status from props when component mounts or when pageParams changes
    if (props.pageParams?.isActive !== undefined) {
      setActivateWorkflow(props.pageParams.isActive);
    }
  }, [props.pageParams]);

  const handleBack = () => {
    if (props.onNavigate) {
      props.onNavigate("work_flow");
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSaveWorkflowStatus = async () => {
    setSaving(true);

    try {
      const token = await getValidToken();

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      // The bulk-update endpoint validates trigger_type against the bot's
      // existing config and rewrites triggers from whatever is sent, so the
      // current values must be resent unchanged alongside is_active.
      const triggerType = props.pageParams?.triggerType;
      const updatePayload = {
        nodebots: [
          {
            id: parseInt(workflowId),
            is_active: activateWorkflow,
            trigger_type: triggerType,
            triggers: triggerType === "message" ? props.pageParams?.triggers || [] : [],
            recurring_start_date: props.pageParams?.recurringStartDate,
            recurring_end_date: props.pageParams?.recurringEndDate,
          },
        ],
      };

      const response = await fetch(NODEBOT_NODEBOTS_BULK_UPDATE, {
        method: "PATCH",
        headers,
        body: JSON.stringify(updatePayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData?.error || "Failed to update workflow status");
      }

      setSnackbar({
        open: true,
        message: "Workflow status updated successfully",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Failed to update workflow status",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { label: "Flow", value: 0 },
    { label: "Nodes", value: 1 },
  ];

  return (
    <Stack sx={{ height: "100%", backgroundColor: "#FAFAFA" }}>
      {/* ── Top Toolbar ── */}
      <Box
        sx={{
          backgroundColor: "#fff",
          borderBottom: "1px solid #E5E7EB",
          px: 2.5,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <BackButton onClick={handleBack} />

          {/* Workflow Icon + Name + Chevron */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              cursor: "pointer",
              "&:hover": { opacity: 0.8 },
            }}
          >
            <AccountTreeOutlinedIcon sx={{ fontSize: 21, color: "#6C7C93" }} />
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: "#0A0B10",
                maxWidth: 220,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {workflowName}
            </Typography>
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#6C7C93" }} />
          </Box>
        </Box>

        {/* Right: Save + Flow/Nodes Tabs */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* Save Workflow Button */}
          <ButtonOne
            variant="contained"
            onClick={handleSaveWorkflowStatus}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} /> : null}
            sx={{
              height: 36,
              px: 2.5,
              whiteSpace: "nowrap",
              fontSize: 13,
              backgroundColor: ColorsConstants.primaryPurple100,
              "&:hover": {
                backgroundColor: ColorsConstants.primaryPurple100,
                opacity: 0.9,
              },
              "&.Mui-disabled": {
                backgroundColor: ColorsConstants.primaryPurple100,
                opacity: 0.6,
              },
            }}
          >
            {saving ? "Saving..." : "Save Workflow"}
          </ButtonOne>

          {/* Flow / Nodes Custom Tabs */}
          <Box sx={{ minWidth: 200 }}>
            <CustomTabs
              value={activeTab}
              onChange={handleTabChange}
              tabs={tabs}
              activeBgColor={ColorsConstants.primaryBackgroundColor}
            />
          </Box>
        </Box>
      </Box>

      {/* ── Tab Content ── */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {activeTab === 0 && (
          <Flow
            workflowId={workflowId}
            workflowName={workflowName}
            isActive={activateWorkflow}
            onStatusChange={setActivateWorkflow}
          />
        )}
        {activeTab === 1 && <Nodes />}
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default ChatbotFlow;
